import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Loader2, ScrollText, Save } from "lucide-react";

const PAGES = [
  { slug: "about", label: "About" },
  { slug: "for-authors", label: "For Authors" },
  { slug: "for-reviewers", label: "For Reviewers" },
  { slug: "peer-review", label: "Peer Review" },
  { slug: "open-access", label: "Open Access" },
  { slug: "publication-ethics", label: "Pub. Ethics" },
];

function PageEditor({ slug }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/pages/${slug}`).then(({ data }) => setForm(data)).catch(() => setForm({ title: "", subtitle: "", body: "" }));
  }, [slug]);

  const save = async () => {
    if (!form.body || !form.body.replace(/<[^>]*>/g, "").trim()) {
      toast.error("Body cannot be empty.");
      return;
    }
    setSaving(true);
    try {
      // Unwrap lists that contentEditable nested inside <p>/<div> so stored HTML stays clean
      const body = form.body
        .replace(/<(p|div)[^>]*>\s*(<(?:ul|ol)[\s\S]*?<\/(?:ul|ol)>)\s*<\/\1>/gi, "$2")
        .replace(/<div>/gi, "<p>")
        .replace(/<\/div>/gi, "</p>");
      await api.put(`/pages/${slug}`, { title: form.title, subtitle: form.subtitle || "", body });
      toast.success("Page saved. Changes are live.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!form) return <div className="py-16 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6 space-y-4 max-w-2xl">
      <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid={`page-title-${slug}`} /></div>
      <div><Label>Subtitle</Label><Input value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="mt-1.5" data-testid={`page-subtitle-${slug}`} /></div>
      <div>
        <Label>Body</Label>
        <p className="text-xs text-slate-400 mb-1.5 mt-0.5">Use the toolbar for bold, italics, bullet or numbered lists, and links.</p>
        <RichTextEditor value={form.body} onChange={(body) => setForm({ ...form, body })} testid={`page-body-${slug}`} />
      </div>
      <Button onClick={save} disabled={saving} className="rounded-full gap-1.5" data-testid={`page-save-${slug}`}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save Page</>}
      </Button>
    </div>
  );
}

export default function PagesAdmin() {
  return (
    <div data-testid="pages-admin">
      <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2"><ScrollText className="w-6 h-6 text-amber-600" /> Editable Pages</h1>
      <p className="text-slate-500 text-sm mb-5">Edit the copy shown on the For Authors, For Reviewers and About pages.</p>
      <Tabs defaultValue="about">
        <TabsList>
          {PAGES.map((p) => <TabsTrigger key={p.slug} value={p.slug} data-testid={`pages-tab-${p.slug}`}>{p.label}</TabsTrigger>)}
        </TabsList>
        {PAGES.map((p) => <TabsContent key={p.slug} value={p.slug} className="mt-5"><PageEditor slug={p.slug} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
